/**
 * CSP-safe 산술 수식 평가기 (Phaser/도메인 비의존, 순수 함수).
 *
 * `{key}` 플레이스홀더를 `params[key]`로 평가 시 주입한 뒤 산술 평가한다.
 * 스탯 강화 / 크래프팅 비용 / 데미지·진행 스케일링 등 "데이터로 정의한 공식"을 런타임 평가하는 데 쓴다.
 *
 * 지원 연산: `+` `-` `*` `/` `**`(거듭제곱, 우결합), 괄호 `( )`, 단항 마이너스,
 * 숫자 리터럴(소수·지수표기 `1e3`). 그 외 토큰(`%`·함수 호출 등)은 미지원 → 0 반환.
 * `eval`/`new Function` 미사용(CSP 안전). 평가 실패·비유한값(Infinity/NaN)은 0으로 폴백.
 *
 * **성능 — 파싱 결과(RPN) 캐시 (⚠ 패키지 레벨 자동 방어):**
 * `{key}`를 문자열 치환하지 않고 **변수 토큰**으로 토크나이즈하므로, 같은 수식 문자열은 params가
 * 달라도 동일한 RPN으로 재사용된다. 토크나이즈+shunting-yard 파싱은 수식별 **1회만** 수행해 모듈 스코프
 * Map에 캐시하고, 평가(`evalRpn`)는 params만 주입해 반복한다. 호출자가 매 프레임 수백 번 호출해도
 * 파싱 비용이 0으로 수렴한다(스탯 xMax 누진비용 루프 등 — 호출자가 이를 몰라도 자동으로 빨라짐).
 *
 * @example
 * evaluateFormula("{base} * {level}", { base: 20, level: 3 })        // 60
 * evaluateFormula("{base} * 1.01 ** {level}", { base: 100, level: 2 }) // 102.01
 * evaluateFormula("2 + 3 * 4")                                          // 14
 */
export function evaluateFormula(expr: string, params: Record<string, number> = {}): number {
  const rpn = rpnFor(expr);
  if (rpn === null) return 0; // 파싱 불가 수식(캐시된 실패) — 기존 동작(폴백 0) 보존
  try {
    const result = evalRpn(rpn, params);
    return Number.isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
}

/**
 * 수식 → RPN(후위표기) 캐시. 파싱 실패는 `null`로 캐시해 재파싱을 막는다.
 * 수식 종류는 데이터(tsv)로 유한하므로 캐시 무한 증가 우려 없음.
 */
const rpnCache = new Map<string, readonly Token[] | null>();

function rpnFor(expr: string): readonly Token[] | null {
  const cached = rpnCache.get(expr);
  if (cached !== undefined) return cached;
  let rpn: readonly Token[] | null;
  try {
    rpn = toRpn(tokenize(expr));
  } catch {
    rpn = null;
  }
  rpnCache.set(expr, rpn);
  return rpn;
}

type Token =
  | { readonly type: "num"; readonly value: number }
  | { readonly type: "var"; readonly name: string }
  | { readonly type: "op"; readonly value: string }
  | { readonly type: "paren"; readonly value: "(" | ")" };

/** 연산자 우선순위 (클수록 먼저). u- = 단항 마이너스. */
const PREC: Record<string, number> = { "+": 2, "-": 2, "*": 3, "/": 3, "u-": 4, "**": 5 };
/** 우결합 연산자 (거듭제곱·단항). */
const RIGHT_ASSOC = new Set(["u-", "**"]);

function tokenize(s: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    if (ch === " " || ch === "\t") {
      i += 1;
      continue;
    }
    // `{key}` 변수 — 치환하지 않고 토큰으로 보존(파싱 캐시 재사용 핵심). 평가 시 params[key] 주입.
    if (ch === "{") {
      const end = s.indexOf("}", i);
      if (end < 0) throw new Error("unclosed var");
      tokens.push({ type: "var", name: s.slice(i + 1, end) });
      i = end + 1;
      continue;
    }
    if (ch === "(" || ch === ")") {
      tokens.push({ type: "paren", value: ch });
      i += 1;
      continue;
    }
    if (ch === "*" && s[i + 1] === "*") {
      tokens.push({ type: "op", value: "**" });
      i += 2;
      continue;
    }
    if (ch === "+" || ch === "-" || ch === "*" || ch === "/") {
      tokens.push({ type: "op", value: ch });
      i += 1;
      continue;
    }
    const m = /^\d*\.?\d+(?:[eE][+-]?\d+)?/.exec(s.slice(i));
    if (m) {
      tokens.push({ type: "num", value: Number(m[0]) });
      i += m[0].length;
      continue;
    }
    throw new Error(`unexpected char: ${ch}`);
  }
  return tokens;
}

/** Shunting-yard: 토큰 배열 → 후위표기(RPN). 단항 마이너스를 위치로 판별. */
function toRpn(tokens: Token[]): Token[] {
  const output: Token[] = [];
  const ops: Token[] = [];
  // 직전 토큰이 값(숫자/변수)/닫는괄호가 아니면 '-'는 단항.
  let prevWasValue = false;
  for (const t of tokens) {
    if (t.type === "num" || t.type === "var") {
      output.push(t);
      prevWasValue = true;
    } else if (t.type === "paren" && t.value === "(") {
      ops.push(t);
      prevWasValue = false;
    } else if (t.type === "paren") {
      while (ops.length && ops[ops.length - 1]!.type !== "paren") output.push(ops.pop()!);
      if (!ops.length) throw new Error("mismatched paren");
      ops.pop();
      prevWasValue = true;
    } else {
      const o1 = t.value === "-" && !prevWasValue ? "u-" : t.value;
      while (ops.length) {
        const top = ops[ops.length - 1]!;
        if (top.type !== "op") break;
        const higher = PREC[top.value]! > PREC[o1]!;
        const equalLeft = PREC[top.value]! === PREC[o1]! && !RIGHT_ASSOC.has(o1);
        if (higher || equalLeft) output.push(ops.pop()!);
        else break;
      }
      ops.push({ type: "op", value: o1 });
      prevWasValue = false;
    }
  }
  while (ops.length) {
    const top = ops.pop()!;
    if (top.type === "paren") throw new Error("mismatched paren");
    output.push(top);
  }
  return output;
}

function evalRpn(rpn: readonly Token[], params: Record<string, number>): number {
  const stack: number[] = [];
  for (const t of rpn) {
    if (t.type === "num") {
      stack.push(t.value);
      continue;
    }
    if (t.type === "var") {
      // 미존재 키 → NaN → 결과 비유한 → 0 폴백(기존 "미치환 `{key}` 잔존 시 0"과 동일 결과).
      stack.push(params[t.name] ?? NaN);
      continue;
    }
    if (t.type !== "op") throw new Error("bad token");
    if (t.value === "u-") {
      const a = stack.pop();
      if (a === undefined) throw new Error("bad expr");
      stack.push(-a);
      continue;
    }
    const b = stack.pop();
    const a = stack.pop();
    if (a === undefined || b === undefined) throw new Error("bad expr");
    switch (t.value) {
      case "+":
        stack.push(a + b);
        break;
      case "-":
        stack.push(a - b);
        break;
      case "*":
        stack.push(a * b);
        break;
      case "/":
        stack.push(a / b);
        break;
      case "**":
        stack.push(a ** b);
        break;
      default:
        throw new Error("bad op");
    }
  }
  if (stack.length !== 1) throw new Error("bad expr");
  return stack[0]!;
}
